import { BufferTarget, EncodedPacket, EncodedVideoPacketSource, MkvOutputFormat, MovOutputFormat, Mp4OutputFormat, Output, StreamTarget, WebMOutputFormat } from 'mediabunny';
import { Color, path, Vec3 } from 'playcanvas';

import { ElementType } from './element';
import { Events } from './events';
import { PngCompressor } from './png-compressor';
import { Scene } from './scene';
import { Splat } from './splat';
import { localize } from './ui/localization';

const nullClr = new Color(0, 0, 0, 0);

// Lookup maps for video output format and codec configuration
const FORMAT_CONFIG: Record<string, { create: (streaming: boolean) => Mp4OutputFormat | MovOutputFormat | MkvOutputFormat | WebMOutputFormat; extension: string }> = {
    mp4: { create: streaming => new Mp4OutputFormat({ fastStart: streaming ? false : 'in-memory' }), extension: 'mp4' },
    webm: { create: () => new WebMOutputFormat(), extension: 'webm' },
    mov: { create: streaming => new MovOutputFormat({ fastStart: streaming ? false : 'in-memory' }), extension: 'mov' },
    mkv: { create: () => new MkvOutputFormat(), extension: 'mkv' }
};

const CODEC_CONFIG: Record<string, { type: 'avc' | 'hevc' | 'vp9' | 'av1'; codec: (height: number) => string }> = {
    h264: { type: 'avc', codec: h => (h < 1080 ? 'avc1.420028' : 'avc1.640033') }, // H.264 Constrained Baseline/High profile
    h265: { type: 'hevc', codec: () => 'hev1.1.6.L120.B0' },                       // H.265 Main profile, Level 4.0
    vp9: { type: 'vp9', codec: () => 'vp09.00.10.08' },                            // VP9 Profile 0, Level 1.0
    av1: { type: 'av1', codec: () => 'av01.0.05M.08' }                             // AV1 Main Profile, Level 3.1
};

type ImageSettings = {
    width: number;
    height: number;
    transparentBg: boolean;
    showDebug: boolean;
};

type VideoSettings = {
    startFrame: number;
    endFrame: number;
    frameRate: number;
    width: number;
    height: number;
    bitrate: number;
    transparentBg: boolean;
    showDebug: boolean;
    includeAnnotations: boolean;
    format: 'mp4' | 'webm' | 'mov' | 'mkv';
    codec: 'h264' | 'h265' | 'vp9' | 'av1';
};

type AnnotationRecordInput = {
    kind?: string;
    position?: number[];
    title?: string;
};

type ExportAnnotationMarker = {
    position: Vec3;
    indexLabel: string;
    title: string;
};

const removeExtension = (filename: string) => {
    return filename.substring(0, filename.length - path.getExtension(filename).length);
};

function volugraphVideoExportBasename(fallback: string): string {
    const cfg = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphVideoExportBasename?: string } })
        .__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
    const base = typeof cfg?.volugraphVideoExportBasename === 'string' ? cfg.volugraphVideoExportBasename.trim() : '';
    if (base) {
        return removeExtension(base);
    }
    const cleaned = removeExtension(fallback);
    return cleaned === 'supersplat' ? 'volugraph-flythrough' : cleaned;
}

const downloadFile = (arrayBuffer: ArrayBuffer, filename: string) => {
    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const el = document.createElement('a');
    el.download = filename;
    el.href = url;
    el.click();
    window.URL.revokeObjectURL(url);
};

const normalizeExportTitle = (title: string, fallbackIndex: number): string => {
    const trimmed = title.trim();
    if (trimmed) {
        return trimmed.replace(/\s+/g, ' ');
    }
    return `Annotation ${fallbackIndex}`;
};

let annotationOverlayCanvas: HTMLCanvasElement | null = null;
let annotationOverlayCtx: CanvasRenderingContext2D | null = null;

const ensureAnnotationOverlayCanvas = (width: number, height: number) => {
    if (!annotationOverlayCanvas || annotationOverlayCanvas.width !== width || annotationOverlayCanvas.height !== height) {
        annotationOverlayCanvas = document.createElement('canvas');
        annotationOverlayCanvas.width = width;
        annotationOverlayCanvas.height = height;
        annotationOverlayCtx = annotationOverlayCanvas.getContext('2d', { alpha: true });
    }
    return {
        canvas: annotationOverlayCanvas,
        ctx: annotationOverlayCtx as CanvasRenderingContext2D
    };
};

const fillRoundedRectPath = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    radius: number
) => {
    const r = Math.max(0, Math.min(radius, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
};

const truncateCanvasText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string => {
    if (!text || maxWidth <= 0) return '';
    if (ctx.measureText(text).width <= maxWidth) return text;
    const ellipsis = '…';
    let trimmed = text;
    while (trimmed.length > 1 && ctx.measureText(`${trimmed}${ellipsis}`).width > maxWidth) {
        trimmed = trimmed.slice(0, -1);
    }
    return `${trimmed}${ellipsis}`;
};

const blendOverlayOntoBuffer = (buffer: Uint8Array, overlay: Uint8ClampedArray) => {
    for (let i = 0; i < buffer.length; i += 4) {
        const alpha = overlay[i + 3] / 255;
        if (alpha <= 0) continue;
        const inv = 1 - alpha;
        buffer[i] = Math.round(buffer[i] * inv + overlay[i] * alpha);
        buffer[i + 1] = Math.round(buffer[i + 1] * inv + overlay[i + 1] * alpha);
        buffer[i + 2] = Math.round(buffer[i + 2] * inv + overlay[i + 2] * alpha);
        buffer[i + 3] = 255;
    }
};

const drawAnnotationMarkers = (
    buffer: Uint8Array,
    width: number,
    height: number,
    markers: ExportAnnotationMarker[],
    scene: Scene
) => {
    if (markers.length === 0) return;

    const { ctx } = ensureAnnotationOverlayCanvas(width, height);
    ctx.clearRect(0, 0, width, height);

    const scale = height / 1080;
    const markerRadius = Math.max(10, Math.round(12 * scale));
    const indexFontSize = Math.max(11, Math.round(13 * scale));
    const titleFontSize = Math.max(12, Math.round(14 * scale));
    const screen = new Vec3();

    for (let i = 0; i < markers.length; i++) {
        const marker = markers[i];
        scene.camera.worldToScreen(marker.position, screen);
        if (screen.z <= 0) continue;

        const x = screen.x * width;
        const y = screen.y * height;
        if (x < -60 || y < -60 || x > width + 60 || y > height + 60) continue;

        ctx.beginPath();
        ctx.arc(x, y, markerRadius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.74)';
        ctx.fill();
        ctx.lineWidth = Math.max(1.5, 2 * scale);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.94)';
        ctx.stroke();

        ctx.font = `700 ${indexFontSize}px Inter, system-ui, -apple-system, sans-serif`;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(marker.indexLabel, x, y);

        const title = normalizeExportTitle(marker.title, Number(marker.indexLabel) || i + 1);
        const titleGap = Math.max(8, Math.round(10 * scale));
        const padX = Math.max(8, Math.round(10 * scale));
        const padY = Math.max(5, Math.round(6 * scale));
        const badgeRadius = Math.max(4, Math.round(6 * scale));
        const maxTitleWidth = Math.max(48, width - x - markerRadius - titleGap - 12);

        ctx.font = `600 ${titleFontSize}px Inter, system-ui, -apple-system, sans-serif`;
        const displayTitle = truncateCanvasText(ctx, title, maxTitleWidth);
        if (!displayTitle) continue;

        const titleWidth = ctx.measureText(displayTitle).width;
        const badgeWidth = titleWidth + padX * 2;
        const badgeHeight = titleFontSize + padY * 2;
        const badgeX = x + markerRadius + titleGap;
        const badgeY = y - badgeHeight / 2;

        if (badgeX + badgeWidth > width - 4) continue;

        fillRoundedRectPath(ctx, badgeX, badgeY, badgeWidth, badgeHeight, badgeRadius);
        ctx.fillStyle = 'rgba(12, 14, 20, 0.88)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
        ctx.lineWidth = Math.max(1, scale);
        ctx.stroke();

        ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(displayTitle, badgeX + padX, y);
    }

    const overlay = ctx.getImageData(0, 0, width, height).data;
    blendOverlayOntoBuffer(buffer, overlay);
};

const registerRenderEvents = (scene: Scene, events: Events) => {
    let compressor: PngCompressor;

    // wait for postrender to fire
    const postRender = () => {
        return new Promise<boolean>((resolve, reject) => {
            const handle = scene.events.on('postrender', () => {
                handle.off();
                try {
                    resolve(true);
                } catch (error) {
                    reject(error);
                }
            });
        });
    };

    events.function('render.offscreen', async (width: number, height: number): Promise<Uint8Array> => {
        try {
            // start rendering to offscreen buffer only
            scene.camera.startOffscreenMode(width, height);
            scene.camera.renderOverlays = false;
            scene.camera.renderPoints = false;
            scene.gizmoLayer.enabled = false;
            scene.pointsLayer.enabled = false;

            // render the next frame
            scene.forceRender = true;

            // for render to finish
            await postRender();

            // cpu-side buffer to read pixels into
            const data = new Uint8Array(width * height * 4);

            const { mainTarget, workTarget } = scene.camera;

            scene.dataProcessor.copyRt(mainTarget, workTarget);

            // read the rendered frame
            await workTarget.colorBuffer.read(0, 0, width, height, { renderTarget: workTarget, data });

            // flip y positions to have 0,0 at the top
            let line = new Uint8Array(width * 4);
            for (let y = 0; y < height / 2; y++) {
                line = data.slice(y * width * 4, (y + 1) * width * 4);
                data.copyWithin(y * width * 4, (height - y - 1) * width * 4, (height - y) * width * 4);
                data.set(line, (height - y - 1) * width * 4);
            }

            return data;
        } finally {
            scene.camera.endOffscreenMode();
            scene.camera.renderOverlays = true;
            scene.camera.renderPoints = true;
            scene.gizmoLayer.enabled = true;
            scene.pointsLayer.enabled = true;
            scene.camera.camera.clearColor.set(0, 0, 0, 0);
        }
    });

    events.function('render.image', async (imageSettings: ImageSettings) => {
        events.fire('startSpinner');

        try {
            const { width, height, transparentBg, showDebug } = imageSettings;

            const exportRenderMode = events.invoke('camera.renderMode') as string;
            const wantsPoints = exportRenderMode === 'points' || exportRenderMode === 'mixed';

            // start rendering to offscreen buffer only
            scene.camera.startOffscreenMode(width, height);
            scene.camera.renderOverlays = showDebug;
            scene.camera.renderPoints = wantsPoints;
            scene.gizmoLayer.enabled = false;
            scene.pointsLayer.enabled = wantsPoints;
            if (!transparentBg) {
                scene.camera.clearPass.setClearColor(events.invoke('bgClr'));
            }

            // render the next frame
            scene.forceRender = true;

            // for render to finish
            await postRender();

            // cpu-side buffer to read pixels into
            const data = new Uint8Array(width * height * 4);

            const { mainTarget, workTarget } = scene.camera;

            scene.dataProcessor.copyRt(mainTarget, workTarget);

            // read the rendered frame
            await workTarget.colorBuffer.read(0, 0, width, height, { renderTarget: workTarget, data });

            // construct the png compressor
            if (!compressor) {
                compressor = new PngCompressor();
            }

            const arrayBuffer = await compressor.compress(
                new Uint32Array(data.buffer),
                width,
                height
            );

            // construct filename
            const selected = events.invoke('selection') as Splat;
            const filename = `${removeExtension(selected?.name ?? 'SuperSplat')}-image.png`;

            // download
            downloadFile(arrayBuffer, filename);

            return true;
        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: localize('panel.render.failed'),
                message: `'${error.message ?? error}'`
            });
        } finally {
            scene.camera.endOffscreenMode();
            scene.camera.renderOverlays = true;
            scene.camera.renderPoints = true;
            scene.gizmoLayer.enabled = true;
            scene.pointsLayer.enabled = true;
            scene.camera.clearPass.setClearColor(nullClr);

            events.fire('stopSpinner');
        }
    });

    events.function('render.video', (videoSettings: VideoSettings, fileStream: FileSystemWritableFileStream) => {
        const renderImpl = async () => {
            events.fire('progressStart', localize('panel.render.exporting-video'), true);

            let cancelled = false;
            const cancelHandler = events.on('progressCancel', () => {
                cancelled = true;
            });

            let encoder: VideoEncoder | null = null;

            try {
                const { startFrame, endFrame, frameRate, width, height, bitrate, transparentBg, showDebug, includeAnnotations, format, codec: codecChoice } = videoSettings;

                const target = fileStream ? new StreamTarget(fileStream) : new BufferTarget();

                // Configure output format and codec from lookup maps (default to mp4/h264)
                const formatConfig = FORMAT_CONFIG[format] ?? FORMAT_CONFIG.mp4;
                const outputFormat = formatConfig.create(!!fileStream);
                const fileExtension = formatConfig.extension;

                const codecConfig = CODEC_CONFIG[codecChoice] ?? CODEC_CONFIG.h264;
                const codecType = codecConfig.type;
                const codec = codecConfig.codec(height);

                const output = new Output({
                    format: outputFormat,
                    target
                });

                const videoSource = new EncodedVideoPacketSource(codecType);
                output.addVideoTrack(videoSource, {
                    rotation: 0,
                    frameRate
                });

                await output.start();

                let encoderError: Error | null = null;

                // helper to create and configure a VideoEncoder instance
                const createEncoder = () => {
                    encoderError = null;
                    const enc = new VideoEncoder({
                        output: async (chunk, meta) => {
                            const encodedPacket = EncodedPacket.fromEncodedChunk(chunk);
                            await videoSource.add(encodedPacket, meta);
                        },
                        error: (error) => {
                            encoderError = error;
                        }
                    });
                    enc.configure({ codec, width, height, bitrate });
                    return enc;
                };

                encoder = createEncoder();

                const exportRenderMode = events.invoke('camera.renderMode') as string;
                const wantsPoints = exportRenderMode === 'points' || exportRenderMode === 'mixed';

                // start rendering to offscreen buffer only
                scene.camera.startOffscreenMode(width, height);
                // Keep presenting frames to the visible canvas during export so the flythrough
                // preview moves in sync with annotation overlays (finalPass is off by default).
                scene.camera.finalPass.enabled = true;
                scene.camera.renderOverlays = showDebug;
                scene.camera.renderPoints = wantsPoints;
                scene.gizmoLayer.enabled = false;
                scene.pointsLayer.enabled = wantsPoints;
                if (!transparentBg) {
                    scene.camera.clearPass.setClearColor(events.invoke('bgClr'));
                }
                scene.lockedRenderMode = true;

                // cpu-side buffer to read pixels into
                const data = new Uint8Array(width * height * 4);
                const line = new Uint8Array(width * 4);
                const annotationMarkers: ExportAnnotationMarker[] = [];
                if (includeAnnotations) {
                    try {
                        const raw = (events.invoke('annotation.getRecords') as AnnotationRecordInput[] | null) ?? [];
                        let labelIndex = 0;
                        for (let i = 0; i < raw.length; i++) {
                            const rec = raw[i];
                            if (rec?.kind && rec.kind !== 'rfi') continue;
                            const pos = rec?.position;
                            if (!Array.isArray(pos) || pos.length !== 3) continue;
                            const px = Number(pos[0]);
                            const py = Number(pos[1]);
                            const pz = Number(pos[2]);
                            if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pz)) continue;
                            labelIndex++;
                            annotationMarkers.push({
                                position: new Vec3(px, py, pz),
                                indexLabel: `${labelIndex}`,
                                title: normalizeExportTitle(
                                    typeof rec.title === 'string' ? rec.title : '',
                                    labelIndex
                                )
                            });
                        }
                    } catch {
                        // annotation system not available in this embed mode
                    }
                }

                // remember last camera position so we can skip sorting if the camera didn't move
                const last_pos = new Vec3(0, 0, 0);
                const last_forward = new Vec3(1, 0, 0);

                // helper to sort splats and wait for completion
                const sortAndWait = (splats: Splat[]) => {
                    return Promise.all(splats.map((splat) => {
                        return new Promise<void>((resolve) => {
                            const { instance } = splat.entity.gsplat;
                            instance.sorter.once('updated', resolve);
                            instance.sort(scene.camera.mainCamera);
                            setTimeout(resolve, 1000);
                        });
                    }));
                };

                // prepare the frame for rendering, returns the newly loaded splat if any
                const prepareFrame = async (frameTime: number): Promise<Splat | null> => {
                    // Fire timeline.time for camera animation interpolation
                    events.fire('timeline.time', frameTime);

                    // Wait for PLY sequence to load the frame if present
                    const newSplat = await events.invoke('plysequence.setFrameAsync', Math.floor(frameTime)) as Splat | null;

                    // manually update the camera so position and rotation are correct
                    scene.camera.onUpdate(0);

                    // If a new PLY was loaded, sort and wait for completion
                    if (newSplat) {
                        await sortAndWait([newSplat]);
                    } else {
                        // No new PLY - sort existing splats if camera moved
                        const pos = scene.camera.position;
                        const forward = scene.camera.forward;
                        if (!last_pos.equals(pos) || !last_forward.equals(forward)) {
                            last_pos.copy(pos);
                            last_forward.copy(forward);

                            const splats = (scene.getElementsByType(ElementType.splat) as Splat[]).filter(splat => splat.visible);
                            await sortAndWait(splats);
                        }
                    }

                    return newSplat;
                };

                // capture the current video frame
                const captureFrame = async (frameTime: number) => {
                    const { mainTarget, workTarget } = scene.camera;

                    scene.dataProcessor.copyRt(mainTarget, workTarget);

                    // read the rendered frame
                    await workTarget.colorBuffer.read(0, 0, width, height, { renderTarget: workTarget, data });

                    // flip the buffer vertically
                    for (let y = 0; y < height / 2; y++) {
                        const top = y * width * 4;
                        const bottom = (height - y - 1) * width * 4;
                        line.set(data.subarray(top, top + width * 4));
                        data.copyWithin(top, bottom, bottom + width * 4);
                        data.set(line, bottom);
                    }

                    if (includeAnnotations && annotationMarkers.length > 0) {
                        drawAnnotationMarkers(data, width, height, annotationMarkers, scene);
                    }

                    // construct the video frame
                    const videoFrame = new VideoFrame(data, {
                        format: 'RGBA',
                        codedWidth: width,
                        codedHeight: height,
                        timestamp: Math.floor(1e6 * frameTime),
                        duration: Math.floor(1e6 / frameRate)
                    });

                    // wait for encoder queue to drain if necessary (backpressure handling)
                    while (encoder.encodeQueueSize > 5) {
                        await new Promise<void>((resolve) => {
                            setTimeout(resolve, 1);
                        });
                    }

                    // if the codec was reclaimed (e.g. browser backgrounded the tab),
                    // recreate the encoder and continue
                    let forceKeyFrame = false;
                    if (encoder.state === 'closed' && encoderError?.message?.includes('reclaimed')) {
                        encoder = createEncoder();
                        forceKeyFrame = true;
                    }

                    // check for non-recoverable encoder errors
                    if (encoderError) {
                        videoFrame.close();
                        throw encoderError;
                    }

                    encoder.encode(videoFrame, { keyFrame: forceKeyFrame });
                    videoFrame.close();
                };

                const animFrameRate = events.invoke('timeline.frameRate');
                const duration = Math.max(0, (endFrame - startFrame) / animFrameRate);
                const progressDenominator = duration > 0 ? duration : 1;

                for (let frameTime = 0; frameTime <= duration; frameTime += 1.0 / frameRate) {
                    // check for cancellation
                    if (cancelled) break;

                    // prepare the frame (loads PLY if needed, updates camera, sorts)
                    await prepareFrame(startFrame + frameTime * animFrameRate);

                    // render a frame
                    scene.lockedRender = true;

                    // wait for render to finish
                    await postRender();

                    // wait for capture
                    await captureFrame(frameTime);

                    events.fire('progressUpdate', {
                        progress: 100 * frameTime / progressDenominator
                    });
                }

                // Flush and finalize output
                events.fire('progressUpdate', {
                    progress: 100
                });
                await encoder.flush();
                await output.finalize();

                // Download (skip if cancelled -- the caller will delete the file)
                if (!cancelled && !fileStream) {
                    const currentSplats = (scene.getElementsByType(ElementType.splat) as Splat[]).filter(splat => splat.visible);
                    const filename = `${volugraphVideoExportBasename(currentSplats[0]?.name ?? 'volugraph-flythrough')}.${fileExtension}`;
                    downloadFile(
                        (output.target as BufferTarget).buffer,
                        filename
                    );
                    events.fire('volugraph.videoExportComplete', { filename });
                }

                return !cancelled;
            } catch (error) {
                await events.invoke('showPopup', {
                    type: 'error',
                    header: localize('panel.render.failed'),
                    message: `'${(error as any).message ?? error}'`
                });
                return false;
            } finally {
                if (encoder && encoder.state !== 'closed') {
                    encoder.close();
                }
                cancelHandler.off();

                scene.camera.endOffscreenMode();
                scene.camera.renderOverlays = true;
                scene.camera.renderPoints = true;
                scene.gizmoLayer.enabled = true;
                scene.pointsLayer.enabled = true;
                scene.camera.clearPass.setClearColor(nullClr);
                scene.lockedRenderMode = false;
                scene.forceRender = true;       // camera likely moved, finish with normal render

                events.fire('progressEnd');
            }
        };

        // Acquire a Web Lock during encoding to signal the browser that this tab is
        // actively working, which helps prevent aggressive background throttling and
        // codec reclamation.
        if (navigator.locks) {
            return navigator.locks.request('supersplat-video-render', renderImpl);
        }
        return renderImpl();
    });
};

export { ImageSettings, VideoSettings, registerRenderEvents };
